import { ChevronRight, PanelLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Tooltip } from "@/components/ui/Tooltip";

export interface Breadcrumb {
  label: string;
  to?: string;
}

/**
 * The 48 px bar at the top of the content frame: panel toggle, breadcrumb trail. Nothing
 * on the right — worker state lives in Settings → Worker, not here.
 *
 * The pipeline stage is the trail's own first link (`useActiveSurface`, see `AppShell.tsx`),
 * so what is on screen is one trail — Sources › Recordings › a meeting's own name — read the
 * way the address itself nests, rather than a stage control and a breadcrumb naming the same
 * place twice.
 */
export function ContextualBar({
  panelOpen,
  onTogglePanel,
  showPanelToggle = true,
  breadcrumbs,
}: {
  panelOpen: boolean;
  onTogglePanel: () => void;
  /**
   * False on an address that has no navigator panel — simple mode's four destinations and
   * its capture page (UI-16). A toggle for a panel that does not exist is a control whose
   * only effect is to do nothing.
   */
  showPanelToggle?: boolean;
  breadcrumbs: Breadcrumb[];
}) {
  return (
    <div className="flex h-12 shrink-0 items-center gap-[var(--space-3)] border-b border-[var(--border-subtle)] px-[var(--space-8)]">
      {showPanelToggle && (
      <Tooltip label={panelOpen ? "Hide the navigator panel" : "Show the navigator panel"}>
        <button
          type="button"
          onClick={onTogglePanel}
          aria-label={panelOpen ? "Hide the navigator panel" : "Show the navigator panel"}
          aria-pressed={panelOpen}
          className="flex h-[26px] w-[26px] items-center justify-center rounded-[var(--radius-md)] text-[var(--text-secondary)] transition-colors duration-[var(--duration-fast)] hover:bg-[var(--surface-2)] hover:text-[var(--text-primary)]"
        >
          <PanelLeft size={15} strokeWidth={2} />
        </button>
      </Tooltip>
      )}

      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1">
        {breadcrumbs.map((crumb, index) => (
          <div key={`${crumb.label}-${index}`} className="flex min-w-0 items-center gap-1">
            {index > 0 && <ChevronRight size={12} strokeWidth={2} className="shrink-0 text-[var(--text-muted)]" />}
            {crumb.to ? (
              <Link
                to={crumb.to}
                className="truncate text-[length:var(--text-xs)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                {crumb.label}
              </Link>
            ) : (
              <span className="truncate text-[length:var(--text-xs)] font-semibold text-[var(--text-primary)]">
                {crumb.label}
              </span>
            )}
          </div>
        ))}
      </nav>
    </div>
  );
}
